import {
  createStore,
  createHook,
  StoreActionApi,
  createContainer,
  defaultRegistry,
} from "react-sweet-state";
import API from "api";
import { encode } from "utils/common";
import { get } from "lodash";
import databases from "cache";
import { FUNCTION_NAME, RESULT_CODE } from "constants/enum";
import { StoreConfig } from "stores/ConfigurationStore/configuration";
import { AuthenticationStates, LoginInformation } from "./authenticationType.d";

export const AUTHENTICATION_STORE = "StoreAuthentication";
type StoreApi = StoreActionApi<AuthenticationStates>;

export const requestLogin = async (values: LoginInformation) => {
  const loginResponse = await API({
    functionName: FUNCTION_NAME.LOGIN,
    method: "POST",
    data: {
      captcha: values.captcha,
      captchaId: values.captchaId,
      isProductCategory: true,
      userPwd: encode(values.password),
    },
    headerOnBody: {
      userName: values.username,
    },
  });

  return loginResponse;
};

export const actions = {
  logout:
    () =>
    async ({ setState, getState }: StoreApi) => {
      const state = getState();
      if (state.loggedIn) {
        setState({
          ...initialState,
        });
        await databases.removeItem(storeKey);
      } else {
        // console.log("Logout called but user is not logged in");
      }
    },
  login:
    (
      values: LoginInformation,
      openNoti: () => void,
      setResultDes: (resultDes: string) => void,
      setCaptchaImage: (captchaImage: string) => void,
      setCaptchaId: (captchaImage: string) => void,
      setIsResetForm: (isResetForm: boolean) => void
    ) =>
    async ({ setState, getState }: StoreApi) => {
      try {
        const response = await requestLogin(values);
        const body = get(response, ["body"], {});
        const resultCode = get(response, ["body", "resultCode"], "");
        const resultDesc = get(response, ["body", "resultDesc"], "");
        const captchaImage = get(response, ["body", "captchaImage"], "");
        const captchaId = get(response, ["body", "captchaId"], "");
        if (resultCode === RESULT_CODE.SUCCESS) {
          setState({
            ...body,
            loggedIn: true,
          });
          const configStore = await defaultRegistry.getStore(StoreConfig);
          const { actions: actionsConfig } = configStore;
          actionsConfig.setIsEndSession(false);
          return;
        }

        if (resultCode !== RESULT_CODE.SUCCESS) {
          setState({
            loggedIn: false,
          });
          setIsResetForm(true);
          setCaptchaImage(captchaImage);
          setCaptchaId(captchaId);
          setResultDes(resultDesc);
          openNoti();
        }
      } catch (e) {
        const userMessage = get(e, "response.data.userMessage", "");
        setResultDes(userMessage);
        openNoti();
        setState({
          loggedIn: false,
        });
      }
    },
  setAuthenticate:
    (auth: any) =>
    ({ setState, getState }: StoreApi) => {
      const prevState = getState();
      setState({
        ...prevState,
        ...auth,
      });
    },
};
type Actions = typeof actions;

export const initialState: AuthenticationStates = {
  loggedIn: false,
  initiated: false,
  account: {
    accountClass: "",
    accountName: "",
    accountNo: "",
    amountLimit: 0,
    availableBalance: 0,
    blockedAmount: 0,
    creditStatus: 0,
    currentBalance: 0,
    debitStatus: 0,
    minBalance: 0,
    modNo: 0,
    numberLimit: 0,
    overdraftAmount: 0,
    primaryAccount: 0,
  },
  captchaTimeout: 0,
  categoryPackage: [],
  customer: {
    addressLine: "",
    cifNo: "",
    coreVVCustomerId: "",
    custTypeId: "",
    customerClass: "",
    customerId: "",
    customerName: "",
    customerNo: "",
    customerStatus: "",
    dateOfIssue: "",
    fullAddress: "",
    identifyId: "",
    identifyLevel: "",
    identifyName: "",
    makerType: "",
    mobilePhone: "",
    placeId: "",
    placeOfIssue: "",
    registerVerifyStatus: "",
    statusCustType: "",
    uniqueId: "",
    uniqueValue: "",
  },
  deviceState: 5,
  havePrimaryAccount: false,
  keyExpired: false,
  notiUnview: 0,
  productCategoryList: [],
  resultCode: "",
  resultDesc: "",
  session: {
    accountNo: "",
    channelCode: "",
    cifCoreBank: "",
    currentLogin: {
      dayOfMonth: 0,
      hourOfDay: 0,
      minute: 0,
      month: 0,
      second: 0,
      year: 0,
    },
    currentSessionTime: {
      dayOfMonth: 0,
      hourOfDay: 0,
      minute: 0,
      month: 0,
      second: 0,
      year: 0,
    },
    custId: "",
    custName: "",
    custNo: "",
    custTypeId: "",
    distributeBranch: "",
    ekycStatus: "",
    email: "",
    identifyId: "",
    isActive: false,
    isFistSignOn: "",
    isGetDirectAccount: false,
    lockUserWeb: "",
    mobilePhone2: "",
    needChangePass: "",
    packageCode: "",
    packageName: "",
    passFailTrans: 0,
    sessionId: "",
    softOtpStatus: "",
    taxCode: "",
    timeOut: 0,
    uniqueId: "",
    uniqueValue: "",
    userGroup: "",
    userId: "",
    userName: "",
    userRole: "",
    userType: "",
    userUniqueValue: "",
    verifyTrans: "",
  },
  tokenSecond: "",
  user: {
    approveStatus: "",
    custId: "",
    custNo: "",
    fullName: "",
    lastModifiedOn: {
      dayOfMonth: 0,
      hourOfDay: 0,
      minute: 0,
      month: 0,
      second: 0,
      year: 0,
    },
    lastPwdChangedOn: {
      dayOfMonth: 0,
      hourOfDay: 0,
      minute: 0,
      month: 0,
      second: 0,
      year: 0,
    },
    lastSignedOn: {
      dayOfMonth: 0,
      hourOfDay: 0,
      minute: 0,
      month: 0,
      second: 0,
      year: 0,
    },
    lastStatusChangedOn: {
      dayOfMonth: 0,
      hourOfDay: 0,
      minute: 0,
      month: 0,
      second: 0,
      year: 0,
    },
    limitDefaultAmount: "",
    mobilePhone2: "",
    needChangePass: "",
    primaryUser: "",
    smsDefaultAmount: "",
    userGroup: "",
    userId: "",
    userName: "",
    userRole: "",
    userStatus: "",
    userType: "",
    verifyTrans: "",
    verifyTransAmount: "",
    verifyTransApp: "",
  },
};

export const StoreAuth = createStore<AuthenticationStates, Actions>({
  initialState,
  actions,
  name: AUTHENTICATION_STORE,
});

export const useAuthentication = createHook(StoreAuth);

export const storeKey = `${StoreAuth.key}@__global__`;

type StoreContainerProps = {
  initialState: AuthenticationStates;
};
export const AuthenticationContainer = createContainer<
  AuthenticationStates,
  Actions,
  StoreContainerProps
>(StoreAuth, {
  onInit:
    () =>
    // eslint-disable-next-line no-shadow
    ({ setState }: StoreApi, { initialState }) => {
      setState({ ...initialState });
    },
});

export default useAuthentication;
